<template>
  <div class="academic-check-management-component">
    <!-- Header Section -->
    <div class="header-section mb-3">
      <h6 class="text-xs fw-semibold text-dark mb-1">
        <i class="fas fa-graduation-cap me-2 text-primary"></i>Academic Check Management
      </h6>
      <p class="text-xs text-muted mb-0">
        Manage and verify academic qualifications for this candidate application.
      </p>
    </div>

    <!-- No Data State -->
    <div
      v-if="!academicCheck"
      class="card no-data-card border rounded-3 mx-auto text-center py-4 px-3"
    >
      <div
        class="card-body p-0 d-flex flex-column align-items-center justify-content-center"
      >
        <span class="no-data-icon mb-3 border rounded p-3">
          <i class="fas fa-book fa-2x text-primary"></i>
        </span>
        <span class="fw-bold text-dark text-xs mb-1"
          >No academic check found</span
        >
        <div class="text-muted text-xs">
          No academic check data available for this customer application.<br />
          Please create or import an academic check record.
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="service-section">
      <!-- Form Section - Pending Status -->
      <div v-if="academicCheck.status === 'pending'" class="form-section">
        <div class="form-section-header pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            Academic Information
          </h6>
          <p class="text-xs text-muted mb-0">
            Please fill in the academic qualification details below.
          </p>
        </div>

        <AcademicCheckForm
          ref="academicForm"
          :initial_data="formData"
          @submit="handleFormSubmit"
          :is_admin="true"
        />

        <!-- Action Buttons -->
        <div class="action-buttons mt-4 d-flex gap-2 justify-content-end">
          <button
            type="button"
            @click="resetForm"
            class="btn btn-outline-secondary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-redo me-1"></i>Reset
          </button>
          <button
            type="button"
            @click="submitForm"
            class="btn btn-bg-secondary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-save me-1"></i>Save Changes
          </button>
          <button
            type="button"
            @click="sendForVerification"
            class="btn btn-bg-success rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-paper-plane me-1"></i>Send for Verification
          </button>
        </div>
      </div>

      <!-- Sent Status Section -->
      <div v-else-if="academicCheck.status === 'sent'" class="sent-section">
        <div class="sent-status-header mb-3 pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            Verification In Progress
          </h6>
          <p class="text-xs text-muted mb-0">
            Your academic check is being processed. Click below to retrieve the verification results.
          </p>
        </div>

        <!-- Subject Info Summary (visible while waiting for results) -->
        <div class="subject-summary mb-3">
          <div class="row g-2">
            <div class="col-md-6" v-if="academicCheck.data?.names">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Names</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.names }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.surname">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Surname</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.surname }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.maiden_name">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Maiden Name</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.maiden_name }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.id_number">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">ID Number</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.id_number }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.date_of_birth">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Date of Birth</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.date_of_birth }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.year">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Year</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.year }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.qualification">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Qualification</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.qualification }}</span>
              </div>
            </div>
            <div class="col-md-6" v-if="academicCheck.data?.document_number">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block">Document Number</span>
                <span class="text-xs fw-semibold text-dark">{{ academicCheck.data.document_number }}</span>
              </div>
            </div>
            <div class="col-12" v-if="academicCheck.data?.subjects && academicCheck.data.subjects.length">
              <div class="border rounded-3 p-2">
                <span class="text-xs text-muted d-block mb-1">Subjects</span>
                <span
                  v-for="(subject, index) in academicCheck.data.subjects"
                  :key="index"
                  class="badge badge-info me-1 mb-1 text-xs"
                >
                  {{ subject }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="card rounded-3 border-info mb-2">
          <div class="card-body text-center py-4">
              <i class="fa fa-info-circle fa-2x text-info"></i>
              <h6 class="text-dark mb-1">Academic Verification Sent</h6>
              <p class="text-xs text-muted mb-0">The verification process may take up to 24 hours. Please check back later to retrieve results.</p>
          </div>
        </div>

        <div class="action-buttons d-flex gap-2 justify-content-end">
          <button
            @click="retrieveData"
            class="btn btn-bg-info rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-download me-1"></i>Retrieve Academic Data
          </button>
        </div>
      </div>

      <!-- Verified Status Section -->
      <div v-else-if="academicCheck.status === 'verified'" class="verified-section">
        <div class="verified-status-header mb-3 pb-3 border-bottom">
          <h6 class="text-xs fw-semibold text-dark mb-1">
            <i class="fas fa-check-circle text-success me-2"></i>Verification Results Retrieved
          </h6>
          <p class="text-xs text-muted mb-0">
            Academic verification results have been retrieved. The report below shows the verification result.
          </p>
        </div>

        <AcademicPost92CheckReport :academic_data="academicCheck.data" />

        <div class="action-buttons mt-3 d-flex gap-2 justify-content-end">
          <button
            @click="editAndResend"
            class="btn btn-outline-secondary rounded-3 btn-sm text-xs"
          >
            <i class="fas fa-edit me-1"></i>Edit and Resend
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import AcademicCheckForm from "./AcademicCheckForm.vue";
import AcademicPost92CheckReport from '@/layouts/pdf_reporting/academic_post92_check/AcademicPost92CheckReport.vue';


export default {
  name: 'AcademicCheckManagement',
  components: {
    AcademicCheckForm,
    AcademicPost92CheckReport
  },
  props: {
    customerApplicationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      academicCheck: null,
      formData: {
        year: '',
        names: '',
        surname: '',
        maiden_name: '',
        id_number: '',
        date_of_birth: '',
        document_number: '',
        qualification: '',
        subjects: []
      },
      originalFormData: {
        year: '',
        names: '',
        surname: '',
        maiden_name: '',
        id_number: '',
        date_of_birth: '',
        document_number: '',
        qualification: '',
        subjects: []
      },
      base_url: API_BASE_URL + "/verify_api",
      isLoading: false,
    }
  },
  mounted() {
    this.loadAcademicCheck();
  },
  methods: {
    async loadAcademicCheck() {
      this.isLoading = true;
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          customer_application_id: this.customerApplicationId
        });
        const response = await fetch(`${this.base_url}/background_screening/academic_check/get-academic-check-by-customer-application/?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.status === 'success') {
          this.academicCheck = data.data.academic_check;
          this.formData = {
            year: this.academicCheck.data?.year || '',
            names: this.academicCheck.data?.names || '',
            surname: this.academicCheck.data?.surname || '',
            maiden_name: this.academicCheck.data?.maiden_name || '',
            id_number: this.academicCheck.data?.id_number || '',
            date_of_birth: this.academicCheck.data?.date_of_birth || '',
            document_number: this.academicCheck.data?.document_number || '',
            qualification: this.academicCheck.data?.qualification || '',
            subjects: this.academicCheck.data?.subjects || []
          };
          this.originalFormData = { ...this.formData };
          console.log('Academic Check Loaded:', this.academicCheck);
          console.log('Form Data:', this.formData);
        } else {
          console.warn('API Response:', data);
        }
      } catch (error) {
        console.error('Error loading academic check:', error);
      } finally {
        this.isLoading = false;
      }
    },

    handleFormSubmit(formData) {
      // Handle the form submission from AcademicCheckForm component
      this.editAcademicCheck(formData);
    },

    submitForm() {
      // Trigger form submission in the AcademicCheckForm component
      this.$refs.academicForm.submitForm();
    },

    async editAcademicCheck(formData) {
      try {
        Swal.fire({
          title: 'Saving...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const token = localStorage.getItem('token');
        const response = await fetch(`${this.base_url}/background_screening/academic_check/admin-edit-academic-check/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            academic_check_id: this.academicCheck.id,
            ...formData
          })
        });
        const data = await response.json();

        if (data.status === 'success') {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Academic check information saved successfully.',
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadAcademicCheck();
        } else {
          throw new Error(data.message || 'Failed to save changes.');
        }
      } catch (error) {
        console.error('Error editing academic check:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to save academic check information.',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async sendForVerification() {
      const confirmed = await Swal.fire({
        icon: 'question',
        title: 'Send for Verification?',
        text: 'Are you sure you want to send this academic check for verification?',
        showCancelButton: true,
        confirmButtonText: 'Yes, Send',
        cancelButtonText: 'Cancel',
      });

      if (!confirmed.isConfirmed) return;

      try {
        Swal.fire({
          title: 'Sending...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const token = localStorage.getItem('token');
        const response = await fetch(`${this.base_url}/background_screening/academic_check/admin-send-academic-check/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            academic_check_id: this.academicCheck.id
          })
        });
        const data = await response.json();

        if (data.status === 'success') {
          this.academicCheck.status = 'sent';
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Academic check has been sent for verification.',
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        } else {
          throw new Error(data.message || 'Failed to send for verification.');
        }
      } catch (error) {
        console.error('Error sending for verification:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to send for verification.',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    async retrieveData() {
      try {
        Swal.fire({
          title: 'Retrieving...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const token = localStorage.getItem('token');
        const response = await fetch(`${this.base_url}/background_screening/academic_check/admin-check-academic-status/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            academic_check_id: this.academicCheck.id
          })
        });
        const data = await response.json();

        if (data.status === 'success' && data.data.response.result.response_data) {
          this.academicCheck.status = 'verified';
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Academic check has been verified successfully.',
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.loadAcademicCheck();
        } else {
          Swal.fire({
            icon: 'info',
            title: 'Data Not Ready',
            text: 'Academic check data is not ready yet.',
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        Swal.fire({
          icon: 'info',
          title: 'Processing',
          text: error.message || 'Verification is still in progress. Please try again later.',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    },

    resetForm() {
      // Reset the form data to original values
      this.formData = { ...this.originalFormData };
      // Also reset the form component if it has a reset method
      if (this.$refs.academicForm && this.$refs.academicForm.resetForm) {
        this.$refs.academicForm.resetForm();
      }
      Swal.fire({
        icon: 'info',
        title: 'Reset',
        text: 'Form has been reset to last saved values.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    },

    validateForm() {
      if (!this.formData.year || !this.formData.year.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please enter the graduation year.',
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }

      if (!this.formData.names || !this.formData.names.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please enter first names.',
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }

      if (!this.formData.surname || !this.formData.surname.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please enter surname.',
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }

      return true;
    },

    goBackToEdit() {
      this.academicCheck.status = 'pending';
    },

    getStatusClass(status) {
      const statusClasses = {
        'pending': 'badge-warning',
        'sent': 'badge-info',
        'verified': 'badge-success'
      };
      return statusClasses[status] || 'badge-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'pending': 'fas fa-hourglass-half',
        'sent': 'fas fa-paper-plane',
        'verified': 'fas fa-check-circle'
      };
      return icons[status] || 'fas fa-circle';
    },

    async editAndResend() {
      const confirmed = await Swal.fire({
        title: 'Edit and Resend Academic Check',
        text: 'This will reset the academic check to pending status with a new reference number. Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Edit and Resend'
      });

      if (!confirmed.isConfirmed) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${this.base_url}/background_screening/academic_check/edit-and-resend-academic-check/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            academic_check_id: this.academicCheck.id
          })
        });
        const data = await response.json();

        if (data.status === 'success') {
          await Swal.fire({
            title: 'Success',
            text: 'Academic check has been reset to pending. You can now edit and resend.',
            icon: 'success',
            confirmButtonText: 'OK'
          });
          // Reload the academic check data
          this.loadAcademicCheck();
        } else {
          throw new Error(data.message || 'Failed to edit and resend');
        }
      } catch (error) {
        console.error('Error editing and resending:', error);
        await Swal.fire({
          title: 'Error',
          text: 'Failed to edit and resend academic check. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }
}
</script>

<style scoped>
/* Component Wrapper */
.academic-check-management-component {
  padding: 0.75rem;
}

/* Header Section */
.header-section {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

/* Service Section Styles */
.service-section {
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem 0.75rem;
}

.service-section:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

/* Status Badge Section */
.status-badge-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  letter-spacing: 0.5px;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-info {
  background-color: #cfe2ff;
  color: #084298;
  border: 1px solid #b6d4fe;
}

.badge-success {
  background-color: #d1e7dd;
  color: #0f5132;
  border: 1px solid #badbcc;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
  border: 1px solid #d3d6d8;
}

/* Form Section */

.form-section-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-buttons .btn {
  padding: 0.375rem 1rem;
  font-weight: 500;
  font-size: 0.75rem;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.action-buttons .btn i {
  margin-right: 0.25rem;
}

.action-buttons .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Status Specific Sections */
.sent-section,
.verified-section,
.form-section {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.sent-status-header,
.verified-status-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

/* Alert Styles */
.alert-sm {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  margin-bottom: 0;
}

.alert-info {
  background-color: #cfe2ff;
  border: 1px solid #b6d4fe;
  color: #084298;
}

/* Verification Data Container */
.verification-data-container {
  padding-top: 1rem;
}

.verification-data-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
}

.verification-data-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.verification-raw-data {
  padding-top: 1rem;
}

.verification-raw-data pre {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #333;
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

/* No Data Card */
.no-data-card {
  background: #fff;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.no-data-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
}

/* Text Utilities */
.fw-semibold {
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .btn {
    width: 100%;
  }

  .verification-data-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .verification-data-item > div {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 576px) {
  .academic-check-management-component {
    padding: 0.5rem;
  }

  .service-section {
    padding: 0.75rem;
  }

  .form-label {
    font-size: 0.7rem;
  }

  .form-control-sm,
  .form-select-sm {
    font-size: 0.8rem;
    padding: 0.3rem 0.4rem;
  }

  .action-buttons .btn {
    padding: 0.3rem 0.75rem;
    font-size: 0.7rem;
  }

  .badge {
    font-size: 0.65rem;
    padding: 0.4rem 0.8rem;
  }
}

/* Print Styles */
@media print {
  .action-buttons {
    display: none;
  }

  .service-section {
    page-break-inside: avoid;
  }
}
</style>